// PDF slideshow renderer untuk player.
//
// Backend mengkonversi file PPT/DOC/XLSX upload ke PDF (LibreOffice headless).
// Player render PDF tersebut sebagai slideshow: tiap halaman tampil bergantian
// dengan auto-loop. Durasi per halaman = total item duration / jumlah halaman.

import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
// Vite akan resolve URL worker file ini ke build asset yang benar.
// `?url` membuat Vite emit file ini dan return URL-nya saja.
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface RenderPdfOptions {
  /** Element root untuk dipasang canvas. Akan di-clear isi sebelumnya. */
  container: HTMLElement;
  /** URL PDF (network atau blob:). */
  pdfUrl: string;
  /** Total durasi seluruh item (detik). Dibagi rata per halaman. */
  totalDurationSec: number;
  /** Minimum durasi per halaman (detik), default 3. */
  minPageDurationSec?: number;
  /** Maximum durasi per halaman (detik), default 30. */
  maxPageDurationSec?: number;
}

/**
 * Render PDF sebagai slideshow di container element.
 * @returns Cleanup function — panggil saat unmount supaya timer & resource dibersihkan.
 */
export async function renderPdfSlideshow(
  opts: RenderPdfOptions,
): Promise<() => void> {
  const { container, pdfUrl, totalDurationSec } = opts;
  const minPage = opts.minPageDurationSec ?? 5;
  const maxPage = opts.maxPageDurationSec ?? 30;

  // Status placeholder saat loading.
  container.innerHTML = `
    <div class="player-placeholder">
      <h1>Memuat PDF...</h1>
    </div>
  `;

  // Load document — pdfjs accept URL atau Uint8Array. URL paling simple.
  // disableWorker false karena worker sudah ke-set di atas.
  const loadingTask = pdfjsLib.getDocument({
    url: pdfUrl,
    // Hindari CORS error di local dev dengan cMapUrl optional.
    cMapPacked: true,
    isEvalSupported: false,
  });

  let pdf: PDFDocumentProxy;
  try {
    pdf = await loadingTask.promise;
  } catch (err) {
    container.innerHTML = `
      <div class="player-placeholder">
        <h1>Gagal memuat PDF</h1>
        <p>${escapeHtml(err instanceof Error ? err.message : String(err))}</p>
      </div>
    `;
    // No-op cleanup karena tidak ada timer yang berjalan.
    return () => {};
  }

  const numPages = pdf.numPages;
  if (numPages === 0) {
    container.innerHTML = `
      <div class="player-placeholder">
        <h1>PDF kosong</h1>
      </div>
    `;
    return () => pdf.destroy();
  }

  // Bagi rata duration per halaman, clamp ke [minPage, maxPage].
  const perPage = Math.max(
    minPage,
    Math.min(maxPage, totalDurationSec / numPages),
  );

  // Setup DOM: container fullscreen black, canvas centered fit-contain.
  container.innerHTML = "";
  container.style.cssText =
    "width:100%; height:100%; background:#000; display:flex; align-items:center; justify-content:center; overflow:hidden;";
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "max-width:100%; max-height:100%; object-fit:contain; background:#fff;";
  container.appendChild(canvas);

  // Hint halaman (e.g. "1 / 12") di pojok kanan-bawah.
  const pageHint = document.createElement("div");
  pageHint.style.cssText =
    "position:absolute; right:1rem; bottom:1rem; padding:4px 10px; border-radius:9999px; background:rgba(0,0,0,0.55); color:#fff; font-size:11px; font-weight:600; font-family:system-ui,sans-serif; letter-spacing:0.05em;";
  // Wrapper untuk positioning absolute hint.
  container.style.position = "relative";
  container.appendChild(pageHint);

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let currentPageIdx = 0;

  const renderPage = async (pageIdx: number) => {
    if (cancelled) return;
    try {
      const page = await pdf.getPage(pageIdx + 1); // pdfjs 1-indexed
      // Hitung scale supaya fit canvas device pixel ratio untuk render sharp.
      const dpr = window.devicePixelRatio || 1;
      const viewport1 = page.getViewport({ scale: 1 });
      const containerW = container.clientWidth || window.innerWidth;
      const containerH = container.clientHeight || window.innerHeight;
      const scale =
        Math.min(containerW / viewport1.width, containerH / viewport1.height) *
        dpr;
      const viewport = page.getViewport({ scale: Math.max(0.5, scale) });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport }).promise;
      pageHint.textContent = `${pageIdx + 1} / ${numPages}`;
    } catch (err) {
      // Skip halaman yang gagal render, lanjut ke berikutnya.
      console.warn("[pdf-renderer] render page failed:", pageIdx + 1, err);
    }
  };

  const cycle = async () => {
    if (cancelled) return;
    await renderPage(currentPageIdx);
    timer = setTimeout(() => {
      currentPageIdx = (currentPageIdx + 1) % numPages;
      void cycle();
    }, perPage * 1000);
  };

  // Start cycle
  void cycle();

  // Cleanup function
  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    void pdf.destroy().catch(() => {});
    // Container akan di-reset oleh renderer luar saat render media berikutnya.
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
